import { inject, Injectable } from '@angular/core';
import {
  selectAllEntitiesApply,
  selectEntity,
  updateEntities,
  upsertEntities,
  upsertEntitiesById,
} from '@ngneat/elf-entities';
import { InteractionService } from '@thiememeulenhoff/ng-api-gateway';
import {
  Attempt,
  InteractionStatus,
  ProgressItem,
  Result,
  SelfScoreType,
  statusGreaterThan,
} from '@thiememeulenhoff/types';
import { combineLatest, filter, first, map, of, switchMap, tap } from 'rxjs';
import { HttpStatusCode } from '@angular/common/http';
import * as stores from './interaction.store';

@Injectable({
  providedIn: 'root',
})
export class InteractionFacade {
  readonly #progressStore = stores.progress;

  readonly #resultsStore = stores.results;

  readonly #interactionService = inject(InteractionService);

  readonly progressById$ = (id: string) => {
    return this.#progressStore.pipe(selectEntity(id));
  };

  readonly progressByParentId$ = (contentIdentifier: string) =>
    this.#progressStore.pipe(
      selectAllEntitiesApply({
        filterEntity: (e) => e.parentIdentifier === contentIdentifier,
      }),
    );

  readonly progressWithChildrenById$ = (id: string) =>
    combineLatest([this.progressById$(id), this.progressByParentId$(id)]).pipe(
      map(([parent, children]): ProgressItem | undefined => {
        if (!parent) {
          return parent;
        }

        return {
          ...parent,
          children,
        } as ProgressItem;
      }),
    );

  readonly resultById$ = (contentIdentifier: string) =>
    this.#resultsStore.pipe(selectEntity(contentIdentifier));

  readonly resultAttemptsById$ = (contentIdentifier: string) =>
    this.#resultsStore.pipe(selectEntity(contentIdentifier));

  fetchProgress(arrangementCode: string, contentIdentifier: string, depth = 1) {
    return this.#interactionService
      .getProgressById(arrangementCode, contentIdentifier, depth)
      .pipe(tap(this.#upsertProgress));
  }

  fetchResult(arrangementCode: string, contentIdentifier: string) {
    return this.#interactionService
      .getResultById(arrangementCode, contentIdentifier)
      .pipe(tap(this.#updateProgressFromResult));
  }

  fetchAttemptsResult(
    arrangementCode: string,
    contentIdentifier: string,
    userIdentifier?: string,
  ) {
    return this.#interactionService
      .getResultAttemptsById(arrangementCode, contentIdentifier, userIdentifier)
      .pipe(tap(this.#updateProgressFromResult));
  }

  fetchAnswer(arrangementCode: string, contentIdentifier: string) {
    return this.#interactionService.getAnswerById(
      arrangementCode,
      contentIdentifier,
    );
  }

  submitAttempt(attempt: Attempt) {
    return this.#interactionService.submitAttempt(attempt);
  }

  saveDraft(draft: Attempt, contentIdentifier: string, isSelfTest: boolean) {
    return this.#interactionService.saveDraft(draft).pipe(
      switchMap((status) => {
        if (
          status === HttpStatusCode.Created ||
          status === HttpStatusCode.NoContent
        ) {
          this.#updateDraft(draft, contentIdentifier, isSelfTest);
        }

        if (isSelfTest && status === HttpStatusCode.ResetContent) {
          return this.fetchResult(draft.arrangementCode, contentIdentifier);
        }

        return of(status);
      }),
    );
  }

  retry(contentIdentifier: string) {
    this.resultById$(contentIdentifier)
      .pipe(first())
      .subscribe((currentResult) => {
        if (currentResult) {
          const result = { ...currentResult, status: InteractionStatus.Draft };
          this.#resultsStore.update(
            upsertEntitiesById(result.contentIdentifier, {
              updater: () => result,
              creator: () => result,
              mergeUpdaterWithCreator: false,
            }),
          );
        }
      });
  }

  startSelfTest(arrangementCode: string, contentPath: string) {
    const contentPathArray = contentPath.split('/');

    return this.#interactionService
      .startSelfTest(arrangementCode, contentPath)
      .pipe(
        switchMap(() =>
          this.fetchProgress(
            arrangementCode,
            contentPathArray[contentPathArray.length - 1],
          ),
        ),
      );
  }

  submitSelfTest(arrangementCode: string, contentPath: string) {
    return this.#interactionService.submitSelfTest(
      arrangementCode,
      contentPath,
    );
  }

  completeSelfTest(arrangementCode: string, contentPath: string) {
    return this.#interactionService.completeSelfTest(
      arrangementCode,
      contentPath,
    );
  }

  deleteSelfTest(arrangementCode: string, contentPath: string) {
    return this.#interactionService.deleteSelfTest(
      arrangementCode,
      contentPath,
    );
  }

  #updateDraft = (
    draft: Attempt,
    contentIdentifier: string,
    isSelfTest: boolean,
  ) => {
    // Note that we patch state with a client side model, draft endpoint does not return a result
    this.resultById$(contentIdentifier)
      .pipe(
        first(),
        tap((currentResult) => {
          if (currentResult) {
            const newResult = { ...currentResult };
            newResult.draft = draft;
            newResult.status =
              // This logic is duplicate of our server side workflow
              isSelfTest && currentResult.numberOfAttempts > 0
                ? InteractionStatus.DraftRetry
                : InteractionStatus.Draft;

            this.#updateProgressFromResult(newResult);
          }
        }),
      )
      .subscribe();
  };

  submitSelfScore(selfScore: SelfScoreType) {
    return this.#interactionService.submitSelfScore(selfScore);
  }

  closeAttempt(arrangementCode: string, contentIdentifier: string) {
    return this.#interactionService.closeAttempt(
      arrangementCode,
      contentIdentifier,
    );
  }

  clearResult(arrangementCode: string, contentIdentifier: string) {
    return this.#interactionService.clearResultById(
      arrangementCode,
      contentIdentifier,
    );
  }

  // #region Store mutations
  #upsertProgress = (item: ProgressItem) => {
    const entities = InteractionFacade.flattenProgress(item);
    this.#progressStore.update(upsertEntities(entities));

    entities.forEach((entity) => {
      this.resultById$(entity.contentIdentifier)
        .pipe(
          first(),
          filter((currentResult) => !!currentResult),
        )
        .subscribe((currentResult) => {
          const newResult = { ...currentResult };

          if (entity.status) {
            newResult.status = entity.status as InteractionStatus;
          }

          this.#resultsStore.update(
            upsertEntitiesById(newResult.contentIdentifier, {
              updater: () => ({ ...newResult }),
              creator: () => ({ ...newResult }),
              mergeUpdaterWithCreator: false,
            }),
          );
        });
    });
  };

  #updateProgress = (
    contentIdentifier: string,
    item: Partial<ProgressItem>,
  ) => {
    this.#progressStore.update(updateEntities(contentIdentifier, item));
  };

  #updateProgressFromResult = (result: Result) => {
    const progress = {
      score: result?.result?.score,
      status: result.status,
      done: statusGreaterThan(result.status, InteractionStatus.Completed),
    };
    this.#updateProgress(result.contentIdentifier, progress);
    this.#resultsStore.update(
      upsertEntitiesById(result.contentIdentifier, {
        updater: () => ({ ...result }),
        creator: () => ({ ...result }),
        mergeUpdaterWithCreator: false,
      }),
    );
  };
  // #endregion

  static flattenProgress(
    progress: ProgressItem,
    parent: ProgressItem | undefined = undefined,
  ) {
    const entities: Array<ProgressItem> = [];
    const children = progress.children || [];
    const copy = { ...progress };
    delete copy.children;

    entities.push({
      ...copy,
      parentIdentifier: parent?.contentIdentifier ?? '',
      done: statusGreaterThan(
        progress.status as InteractionStatus,
        InteractionStatus.Completed,
      ),
    });

    children.forEach((child) => {
      entities.push(...InteractionFacade.flattenProgress(child, copy));
    });

    return entities;
  }
}
